import { useCallback, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import hotToast from 'react-hot-toast';

/**
 * Toast helpers for Pro mobile pages.
 *
 * Uses `react-hot-toast` (mounted globally in main.tsx) because Ionic's
 * overlay system is broken on this app — the `ion-app { contain: none }`
 * override in index.css disables the portal that IonToast / IonSelect /
 * IonAlert / IonActionSheet rely on. `react-hot-toast` mounts its own
 * portal at `document.body`, sidestepping that whole subsystem.
 */

export interface PendingToast {
  message: string;
  color?: 'success' | 'danger' | 'primary';
}

/** Pro mobile toast hook — shows success / error via the global Toaster. */
export const useProToast = () => {
  const showSuccess = useCallback((message: string) => {
    hotToast.success(message);
  }, []);

  const showError = useCallback((message: string) => {
    hotToast.error(message);
  }, []);

  return { showSuccess, showError };
};

/**
 * Cross-page toast hand-off. A page about to navigate calls
 * `navigate(path, { state: navStateWithToast({...}) })` so the destination
 * page (using `usePendingToast`) can pop the toast on mount. Still useful
 * with the global Toaster: it lets the source page navigate immediately
 * and ensures the success message lands on the screen the user expects.
 */
export const navStateWithToast = (toast: PendingToast) => ({ toast });

/**
 * Drop-in replacement for `useProToast` on pages that should also pop a
 * one-shot toast carried in React Router's navigation state. After
 * displaying the pending toast once, clears history state so a refresh /
 * back-navigation doesn't replay it.
 */
export const usePendingToast = () => {
  const api = useProToast();
  const location = useLocation();
  const navigate = useNavigate();
  const consumed = useRef(false);

  useEffect(() => {
    if (consumed.current) return;
    const pending = (location.state as { toast?: PendingToast } | null)?.toast;
    if (!pending?.message) return;
    consumed.current = true;
    if (pending.color === 'danger') api.showError(pending.message);
    else api.showSuccess(pending.message);
    // Strip the toast off the current history entry so back / refresh
    // doesn't re-fire it. `replace: true` keeps the back-stack clean.
    navigate(location.pathname, { replace: true, state: null });
  }, [location, navigate, api]);

  return api;
};
